import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useCRUD } from '../../../api/crud';
import { USERS_URL } from '../../../api/users';
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../../consts';
import { User } from '../../../types/users';

type DeleteType = 'soft' | 'hard';

interface RemoveUserProps {
  user?: User;
  onSuccess?: () => void;
}

export const RemoveUser: React.FC<RemoveUserProps> = ({ user, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { loading, error, remove } = useCRUD(USERS_URL);
  const [deleteType, setDeleteType] = useState<DeleteType>('soft');
  const userName = user?.first_name + ' ' + user?.last_name;

  const handleSubmit = async () => {
    if (loading || !user) {
      return;
    }

    const result = await remove({
      id: user.id,
      params: deleteType === 'hard' ? { hard_delete: 'true' } : undefined,
    });

    if (result?.success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(
        deleteType === 'hard'
          ? t('User permanently deleted')
          : t('User removed from workspace successfully'),
        SUCCESS_SNACKBAR
      );
      navigate('/tenant/users');
      onSuccess?.();
    } else {
      enqueueSnackbar(t('Failed to remove user from workspace'), ERROR_SNACKBAR);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('How do you want to remove this user from the workspace?')}
      </Typography>

      <FormControl component="fieldset">
        <RadioGroup
          value={deleteType}
          onChange={(e) => setDeleteType(e.target.value as DeleteType)}
        >
          <FormControlLabel
            value="soft"
            control={<Radio />}
            label={t('Remove from workspace (recommended) — Revoke access only. The user remains in the workspace database and can be re-activated later.')}
          />
          <FormControlLabel
            value="hard"
            control={<Radio />}
            label={t('Permanently delete user — Remove the user from the workspace and delete their account from the database. This cannot be undone.')}
          />
        </RadioGroup>
      </FormControl>

      <Alert severity="warning">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('User')}: {userName}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm')}
      </Button>
    </Box>
  );
};
